// Planning Command Center — shell. Top-level page hosts all 9 sub-pages
// from the prototype, each linked via a top tab nav. Sub-pages live in
// /pages/planning/ and read from existing HQ data (no duplicate logic).
import { Link, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "../App";

import PccDashboard from "./planning/PccDashboard";
import PccProjectDetail from "./planning/PccProjectDetail";
import PccEstimator from "./planning/PccEstimator";
import PccGantt from "./planning/PccGantt";
import PccCapacity from "./planning/PccCapacity";
import PccPayments from "./planning/PccPayments";
import PccSalesBridge from "./planning/PccSalesBridge";
import PccSuppliers from "./planning/PccSuppliers";
import PccActivity from "./planning/PccActivity";
import PccArchived from "./planning/PccArchived";
import { ALLOWED_ROLES } from "./planning/pccHelpers";

const TABS = [
  { id: "", label: "Dashboard" },
  { id: "estimator", label: "Estimator" },
  { id: "gantt", label: "Gantt" },
  { id: "capacity", label: "Capacity" },
  { id: "payments", label: "Payments" },
  { id: "sales", label: "Sales" },
  { id: "suppliers", label: "Suppliers" },
  { id: "activity", label: "Activity" },
  { id: "archived", label: "Archived" },
];

function TabNav() {
  const loc = useLocation();
  const sub = loc.pathname.replace(/^\/planning-command-center\/?/, "").split("/")[0] || "";
  return (
    <div className="flex items-center gap-1 flex-wrap mb-5 border-b border-white/[0.08] pb-0" data-testid="pcc-tab-nav">
      {TABS.map(t => {
        const active = sub === t.id;
        return (
          <Link
            key={t.id}
            to={`/planning-command-center${t.id ? "/" + t.id : ""}`}
            className={`relative px-4 py-2.5 text-[12px] uppercase tracking-[0.08em] font-semibold transition-colors ${
              active ? "text-white" : "text-white/45 hover:text-white/75"
            }`}
            data-testid={`pcc-tab-${t.id || "dashboard"}`}
          >
            {t.label}
            {active && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-amber-400 rounded-full" />}
          </Link>
        );
      })}
    </div>
  );
}

export default function PlanningCommandCenter() {
  const { user } = useAuth();
  const canAccess = ALLOWED_ROLES.has(user?.role);

  if (!canAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="pcc-access-denied">
        <Card className="bg-[#000000] border-white/[0.08] p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h2 className="text-white text-xl font-light">Access restricted</h2>
          <p className="text-white/60 text-sm mt-2">Planning Command Center is available to PM, Production, Sales, and Executive roles. Ask an admin to update your access.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="mb-3">
          <h1 className="text-2xl font-light tracking-tight">Planning Command Center</h1>
        </div>
        <TabNav />
        <Routes>
          <Route index element={<PccDashboard />} />
          <Route path="estimator" element={<PccEstimator />} />
          <Route path="gantt" element={<PccGantt />} />
          <Route path="capacity" element={<PccCapacity />} />
          <Route path="payments" element={<PccPayments />} />
          <Route path="sales" element={<PccSalesBridge />} />
          <Route path="suppliers" element={<PccSuppliers />} />
          <Route path="activity" element={<PccActivity />} />
          <Route path="archived" element={<PccArchived />} />
          <Route path="projects/:id" element={<PccProjectDetail />} />
          <Route path="*" element={<Navigate to="/planning-command-center" replace />} />
        </Routes>
      </div>
    </div>
  );
}
